import React, { useCallback, useContext, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import Tooltip, { tooltipClasses, type TooltipProps } from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { styled } from "@mui/material/styles";
import { useForkRef } from "@mui/material/utils";
import { TooltipComponentProps } from "../../interface";
import {
  actsOnTap,
  PRIMARY_INPUT_CAN_HOVER,
  revealsOnTap,
  TAP_REVEAL_ATTR,
  TAP_TOOLTIP_ATTR,
  TOOLTIP_ONLY_ATTR,
} from "../../utils/touchInput";
import { useDragStart } from "../DraggablePanel/dragStart";

/**
 * MUI's Tooltip props, with an onOpen that can also be handed a native click: on a touch-only device the tap that
 * opens the shared tooltip reaches it through a listener on its anchor, not through a React handler.
 */
export type SharedTooltipProps = Omit<TooltipProps, "onOpen"> & {
  onOpen?: ((event: React.SyntheticEvent | Event) => void) | undefined;
};

const NATIVE_CONTROLS = new Set(["a", "button", "input", "select", "textarea", "summary"]);

const propOf = (element: React.ReactElement<unknown>, name: string): unknown => {
  const { props } = element;
  if (typeof props !== "object" || props === null) return undefined;
  const value: unknown = Reflect.get(props, name);
  return value;
};

/**
 * Whether a tap on `child` can be what opens the tooltip it anchors: it is marked tooltip-only (TOOLTIP_ONLY_ATTR),
 * or it has no onClick, is no native control and is not disabled. What sits around the anchor is judged at the
 * tap, by revealsOnTap.
 */
export function isTapAnchor(child: React.ReactNode): boolean {
  if (!React.isValidElement(child)) return false;
  if (propOf(child, TOOLTIP_ONLY_ATTR) !== undefined) return true;
  if (typeof child.type === "string" && NATIVE_CONTROLS.has(child.type)) return false;
  return propOf(child, "onClick") === undefined && !propOf(child, "disabled");
}

/** Clicks a tooltip anchored inside another one already took: the tooltips around it leave them, one per tap. */
const takenTaps = new WeakSet<Event>();

/** The tooltip a touch-only device has open, as the one capture-phase click listener below sees it. */
interface OpenTooltip {
  readonly anchor: Element;
  close(event: Event): void;
}

let trackedTooltip: OpenTooltip | null = null;

/**
 * Closes the open tooltip on a click anywhere but its anchor, whose own listener toggles it. In the capture phase,
 * so a click whose handler stops propagation still closes it.
 */
function closeOnClickElsewhere(event: MouseEvent): void {
  if (trackedTooltip && !event.composedPath().includes(trackedTooltip.anchor)) trackedTooltip.close(event);
}

/**
 * Makes `tooltip` the one open tooltip, closing the one open before, and returns what forgets it again. While one
 * is open, a single click listener on the document closes it.
 */
export function trackOpenTooltip(tooltip: OpenTooltip): () => void {
  const previous = trackedTooltip;
  trackedTooltip = tooltip;
  if (previous) previous.close(new Event("close"));
  else document.addEventListener("click", closeOnClickElsewhere, true);
  return () => {
    if (trackedTooltip !== tooltip) return;
    trackedTooltip = null;
    document.removeEventListener("click", closeOnClickElsewhere, true);
  };
}

/** How many tooltips open from a tap on each anchor: a tooltip around a component that renders its own shares it. */
const tapAnchorUsers = new WeakMap<Element, number>();

/** Marks `anchor` as one a tap opens a tooltip from (TAP_TOOLTIP_ATTR), until the returned function is called. */
function markTapAnchor(anchor: Element): () => void {
  tapAnchorUsers.set(anchor, (tapAnchorUsers.get(anchor) ?? 0) + 1);
  anchor.setAttribute(TAP_TOOLTIP_ATTR, "");
  return () => {
    const left = (tapAnchorUsers.get(anchor) ?? 1) - 1;
    tapAnchorUsers.set(anchor, left);
    if (left === 0) anchor.removeAttribute(TAP_TOOLTIP_ATTR);
  };
}

/** The element MUI's Tooltip anchors to, kept in state, and the ref that hands it over, forked with the caller's. */
function useAnchor(ref: React.Ref<unknown> | undefined): [Element | null, React.RefCallback<unknown> | null] {
  const [anchor, setAnchor] = useState<Element | null>(null);
  const setAnchorNode = useCallback((node: unknown) => setAnchor(node instanceof Element ? node : null), []);
  return [anchor, useForkRef(ref, setAnchorNode)];
}

/** The anchor of the tooltip around this one, when a tap opens that one. */
const EnclosingTapAnchor = React.createContext<Element | null>(null);

/** The one part of popper.js's instance an open tooltip uses: placing it again next to an anchor that moved. */
interface PopperHandle {
  update(): unknown;
}

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
  if (typeof ref === "function") ref(value);
  else if (ref) ref.current = value;
}

/** The slot props with a popper slot whose popperRef also hands popper.js's instance to `own`. */
function withPopperHandle(
  slotProps: TooltipProps["slotProps"],
  own: React.RefObject<PopperHandle | null>,
): TooltipProps["slotProps"] {
  const popperSlot = slotProps?.popper;
  const fork =
    <T extends PopperHandle>(callerRef: React.Ref<T> | undefined): React.RefCallback<T> =>
    (instance) => {
      own.current = instance;
      assignRef(callerRef, instance);
    };
  const className = (callerClass: string | undefined): string =>
    callerClass === undefined ? TOUCH_POPPER_CLASS : `${callerClass} ${TOUCH_POPPER_CLASS}`;
  if (typeof popperSlot === "function") {
    return {
      ...slotProps,
      popper: (ownerState) => {
        const resolved = popperSlot(ownerState);
        return { ...resolved, popperRef: fork(resolved.popperRef), className: className(resolved.className) };
      },
    };
  }
  return {
    ...slotProps,
    popper: { ...popperSlot, popperRef: fork(popperSlot?.popperRef), className: className(popperSlot?.className) },
  };
}

const sameRect = (a: DOMRect, b: DOMRect): boolean =>
  a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;

/** On a touch-only device's tooltip popper: index.css hides it while its anchor is scrolled out of view. */
const TOUCH_POPPER_CLASS = "touch-tooltip-popper";

/**
 * While `shown`: keeps the tooltip on its anchor, which a dock window or a re-layout can carry away without a scroll
 * or a resize, closes it once the anchor leaves the page (a recycled grid cell), and makes it the one open tooltip,
 * which a click anywhere else closes. Returns the slot props that hand popper.js's instance over.
 */
function useOpenOnTouch(
  shown: boolean,
  anchor: Element | null,
  slotProps: TooltipProps["slotProps"],
  close: (event: Event) => void,
): TooltipProps["slotProps"] {
  const popper = useRef<PopperHandle | null>(null);
  const followingSlotProps = useMemo(() => withPopperHandle(slotProps, popper), [slotProps]);
  const closeNow = useEffectEvent(close);

  useEffect(() => {
    if (!shown || !anchor) return undefined;
    return trackOpenTooltip({ anchor, close: (event) => closeNow(event) });
  }, [shown, anchor]);

  useEffect(() => {
    if (!shown || !anchor) return undefined;
    let placedAt = anchor.getBoundingClientRect();
    let frame = 0;
    const follow = (): void => {
      if (!anchor.isConnected) {
        closeNow(new Event("close"));
        return;
      }
      const rect = anchor.getBoundingClientRect();
      if (!sameRect(rect, placedAt)) {
        placedAt = rect;
        // No popper yet on the frame the tooltip opens; the next frame places it.
        popper.current?.update();
      }
      frame = requestAnimationFrame(follow);
    };
    frame = requestAnimationFrame(follow);
    return () => cancelAnimationFrame(frame);
  }, [shown, anchor]);

  return followingSlotProps;
}

/**
 * MUI's Tooltip where the primary input cannot hover (a phone, a tablet). Neither a hover nor MUI's long press
 * opens it: the mouseover a tap leaves behind is ignored only by the tooltip instance that saw the touch, and a
 * long press is what a scroll or a drag starts with. A tap opens and closes it instead, when the tap does nothing
 * else (revealsOnTap) on an anchor that does nothing of its own (isTapAnchor, and the element rendered for it:
 * actsOnTap) and there is something to show.
 * - One tap opens one tooltip: the innermost anchor takes it. A tooltip whose caller decides when it opens (`open`
 *   with onOpen and onClose, as Text) leaves taps to a tap tooltip whose anchor holds its own, since it cannot say
 *   during the tap whether it will open.
 * - One is open at a time. A click anywhere else closes it, as do Escape, a drag of the DraggablePanel or widget
 *   grid around it, and its anchor leaving the page; a blur too, unless a tap opened it. While open it follows its
 *   anchor (useOpenOnTouch), and it is hidden while the anchor is scrolled out of view.
 * - A caller passing `open` keeps control: a tap calls its onOpen or onClose, and its next `open` says what
 *   happened.
 * - An anchor marked TAP_REVEAL_ATTR takes its tap even inside something that acts on one, and stops it there.
 * Keyboard focus still opens it, and a string title still names the child (MUI sets it as the child's aria-label),
 * the only name an icon-only button has. followCursor is dropped: the point a tap leaves is no anchor to keep.
 */
function TouchOnlyTooltip({
  ref,
  children,
  title,
  open: openProp,
  onOpen,
  onClose,
  slotProps,
  followCursor: _followCursor,
  ...props
}: SharedTooltipProps): React.JSX.Element {
  const [ownOpen, setOwnOpen] = useState(false);
  const [anchor, anchorRef] = useAnchor(ref);
  const enclosing = useContext(EnclosingTapAnchor);

  const controlled = openProp !== undefined;
  const hasTitle = Boolean(title) || title === 0;
  const open = openProp ?? ownOpen;
  const shown = open && hasTitle;
  const qualifies = isTapAnchor(children) && (controlled || hasTitle);

  // Whether the tooltip showing was opened by a tap rather than by keyboard focus. It then stays until it is
  // dismissed, not when focus moves on its own: a grid focuses the cell it was tapped in right after the tap.
  const [openedByTap, setOpenedByTap] = useState(false);

  const show = (event: React.SyntheticEvent | Event, byTap = false): void => {
    setOpenedByTap(byTap);
    if (!controlled) setOwnOpen(true);
    onOpen?.(event);
  };
  const hide = (event: React.SyntheticEvent | Event): void => {
    if (shown && openedByTap && event.type === "blur") return;
    setOpenedByTap(false);
    if (!controlled) setOwnOpen(false);
    onClose?.(event);
  };

  const toggleOnTap = useEffectEvent((event: MouseEvent, tapped: Element): void => {
    if (takenTaps.has(event)) return;
    const { target } = event;
    const stopsHere = tapped.hasAttribute(TAP_REVEAL_ATTR);
    if (!(target instanceof Element) || !(stopsHere || revealsOnTap(target, tapped))) {
      if (shown) hide(event);
      return;
    }
    takenTaps.add(event);
    // The anchor asked for its tap (TAP_REVEAL_ATTR): what it sits in must not act on the same tap.
    if (stopsHere) event.stopPropagation();
    if (shown) hide(event);
    // A click from Enter or Space on a focused tooltip-only button has no pointer behind it (detail 0): that tooltip
    // was opened from the keyboard, and closes on a blur like any other.
    else show(event, event.detail > 0);
  });

  useEffect(() => {
    if (!anchor || !qualifies || actsOnTap(anchor) || (controlled && enclosing?.contains(anchor))) return undefined;
    // A tap's click is a MouseEvent (a PointerEvent in browsers); Element's event map does not type it.
    const onClick = (event: Event): void => {
      if (event instanceof MouseEvent) toggleOnTap(event, anchor);
    };
    const unmark = markTapAnchor(anchor);
    anchor.addEventListener("click", onClick);
    return () => {
      anchor.removeEventListener("click", onClick);
      unmark();
    };
  }, [anchor, qualifies, controlled, enclosing]);

  const followingSlotProps = useOpenOnTouch(shown, anchor, slotProps, hide);

  useDragStart(() => {
    if (shown) hide(new Event("close"));
  });

  return (
    <EnclosingTapAnchor value={qualifies ? anchor : enclosing}>
      <Tooltip
        {...props}
        ref={anchorRef}
        title={title}
        open={open}
        onOpen={(event) => show(event)}
        onClose={hide}
        slotProps={followingSlotProps}
        disableHoverListener
        disableTouchListener
      >
        {children}
      </Tooltip>
    </EnclosingTapAnchor>
  );
}

/**
 * MUI's Tooltip with MUI's own props and look, for a caller that needs those rather than
 * ToolTipComponent's, which renders through it too. Where the primary input can hover (a desktop, a laptop
 * with a touch screen) this is MUI's Tooltip itself.
 */
export const PlainTooltip: (props: SharedTooltipProps) => React.JSX.Element = PRIMARY_INPUT_CAN_HOVER
  ? Tooltip
  : TouchOnlyTooltip;

/** TapTooltip keeps its own open state; a click on its child is what toggles it on a touch-only device. */
export type TapTooltipProps = Omit<TooltipProps, "children" | "open" | "onOpen" | "onClose"> & {
  children: React.ReactElement<{ onClick?: React.MouseEventHandler<HTMLElement> | undefined }>;
};

function TouchOnlyTapTooltip({ ref, children, slotProps, ...props }: TapTooltipProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [anchor, anchorRef] = useAnchor(ref);
  const close = (): void => setOpen(false);

  useEffect(() => (anchor ? markTapAnchor(anchor) : undefined), [anchor]);
  // Closed by a click elsewhere, not a touch: a finger scrolling the page leaves it open. A click inside the tooltip
  // counts as elsewhere, after the control clicked there has run.
  const followingSlotProps = useOpenOnTouch(open, anchor, slotProps, close);
  useDragStart(close);

  return (
    <Tooltip
      {...props}
      ref={anchorRef}
      open={open}
      // Only keyboard focus still opens it (see TouchOnlyTooltip); blur and Escape close it.
      onOpen={() => setOpen(true)}
      onClose={close}
      slotProps={followingSlotProps}
      disableHoverListener
      disableTouchListener
    >
      {React.cloneElement(children, {
        onClick: (event: React.MouseEvent<HTMLElement>) => {
          children.props.onClick?.(event);
          setOpen((wasOpen) => !wasOpen);
        },
      })}
    </Tooltip>
  );
}

/**
 * PlainTooltip for a tooltip that holds controls of its own (a button in it). Where the primary input can hover
 * this is MUI's Tooltip itself. Where it cannot, every click on the child (a tap) opens and closes it, whatever
 * else the click does, and a click anywhere else closes it after the control clicked there has run, as does a
 * drag of the DraggablePanel or widget grid around it; MUI's long-press timer and the mouseover a tap leaves
 * behind still do not open it.
 */
export const TapTooltip: (props: TapTooltipProps) => React.JSX.Element = PRIMARY_INPUT_CAN_HOVER
  ? Tooltip
  : TouchOnlyTapTooltip;

interface StyledTooltipProps extends SharedTooltipProps {
  tooltipStyles?: React.CSSProperties;
}

const StyledTooltip = styled(
  // `className` is injected by styled() but typed optional, so it is spread in only when present:
  // MUI's `TooltipClasses` keys are `string`, and an explicit `undefined` popper class is not the
  // same as an absent one under exactOptionalPropertyTypes.
  ({ className, ...props }: StyledTooltipProps) => (
    <PlainTooltip {...props} classes={{ ...(className !== undefined && { popper: className }) }} />
  ),
  {
    shouldForwardProp: (prop) => prop !== "tooltipStyles",
  },
)(({ tooltipStyles }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "var(--primary-text)",
    color: "var(--surface-white)",
    fontSize: "0.875rem",
    fontWeight: 500,
    fontFamily: "inherit",
    padding: "0.5rem 0.75rem",
    borderRadius: "0.25rem",
    gap: "0.5rem",
    ...tooltipStyles,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "var(--primary-text)",
  },
}));

const TOOLTIP_Z_INDEX = 13000;
// Stable references: a fresh `slots`/`slotProps` object each render makes MUI re-run useSlot and
// remount the Zoom transition, which can schedule an update → re-render → new object → render loop.
const TOOLTIP_SLOTS = { transition: Zoom };

const ToolTipComponent: React.FC<TooltipComponentProps> = (props) => {
  // `showTipPointer` is the Syncfusion name for the arrow, which this tooltip always draws: it is read off here, since
  // MUI hands every prop it does not know to the anchor element, where React warns about it.
  const { content, children, position, slotProps, showTipPointer: _showTipPointer, ...rest } = props;
  const hasContent = !!content;

  const mergedSlotProps = React.useMemo(
    () => ({
      ...slotProps,
      popper: {
        ...slotProps?.popper,
        style: { zIndex: TOOLTIP_Z_INDEX, ...slotProps?.popper?.style },
      },
    }),
    [slotProps],
  );

  return (
    <StyledTooltip
      {...rest}
      slotProps={mergedSlotProps}
      placement={position || "top"}
      slots={TOOLTIP_SLOTS}
      arrow
      title={hasContent ? content : ""}
    >
      {/* MUI clones this child to attach the ref and the hover/focus handlers, so it must be a
          single element. `TooltipComponentProps.children` is a `ReactNode`, so anything that is not
          already an element (text, a number, `false`, nullish) is wrapped instead of asserted. */}
      {React.isValidElement(children) ? children : <span>{children}</span>}
    </StyledTooltip>
  );
};

ToolTipComponent.displayName = "ToolTipComponent";
export default ToolTipComponent;
